import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  computeLds, fetchLds, ncgeListCampaigns, ncgeCreateCampaign,
  ncgeCheckout,
} from "../lib/api";

/**
 * ContractorGrow — "Get More Customers! — Let NearNow.biz Advertise For You!"
 *
 * Plan-gated to Pro / Elite / Market Leader contractors. Flow:
 *   1. Compute Local Dominance Score (LDS) — 7-pillar diagnostic.
 *   2. Generate a campaign brief (12 ads, geo-targeting, funnel design).
 *   3. Pay $150 one-time ($100 ad spend + $50 NearNow management fee).
 *   4. Wait for reviewer approval → NearNow launches in Meta.
 *   5. Track performance on this page.
 */
const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";
const GREY = "#5a5d66";
const BG = "#F8F9FA";

const ContractorGrow = () => {
  const { contractorId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();

  const [lds, setLds] = useState(null);
  const [ldsLoading, setLdsLoading] = useState(false);
  const [ldsErr, setLdsErr] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [overrides, setOverrides] = useState({
    website_url: "", gbp_url: "", facebook_url: "", instagram_url: "",
    google_reviews_count: "", google_reviews_avg: "",
  });
  const [showLdsForm, setShowLdsForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);

  const loadAll = async () => {
    try {
      const ld = await fetchLds(contractorId);
      setLds(ld);
    } catch { /* none yet — fine */ }
    try {
      const cs = await ncgeListCampaigns(contractorId);
      setCampaigns(cs.items || []);
    } catch { /* none */ }
  };

  useEffect(() => { if (contractorId) loadAll(); }, [contractorId]);

  // Success / cancel return from Stripe
  const paidParam = params.get("paid");
  useEffect(() => {
    if (paidParam === "1") {
      // Hard refresh of campaigns since Stripe webhook may need a moment
      const id = setTimeout(loadAll, 2000);
      return () => clearTimeout(id);
    }
  }, [paidParam]);

  const onCompute = async () => {
    setLdsErr(null); setLdsLoading(true);
    try {
      const o = { ...overrides };
      // Strip empty strings
      Object.keys(o).forEach((k) => { if (o[k] === "" || o[k] == null) delete o[k]; });
      if (o.google_reviews_count) o.google_reviews_count = Number(o.google_reviews_count);
      if (o.google_reviews_avg) o.google_reviews_avg = Number(o.google_reviews_avg);
      const r = await computeLds(contractorId, o);
      if (!r.ok) { setLdsErr(r.error || "compute_failed"); return; }
      setLds(r);
      setShowLdsForm(false);
    } catch (e) {
      setLdsErr("server_error");
    } finally {
      setLdsLoading(false);
    }
  };

  const onCreate = async () => {
    setCreateErr(null); setCreating(true);
    try {
      const r = await ncgeCreateCampaign(contractorId, {
        chosen_angle: lds?.recommended_ad_angle || null,
      });
      if (!r.ok) {
        setCreateErr(r.error || "create_failed");
        return;
      }
      // Immediately checkout
      const co = await ncgeCheckout(r.id, window.location.origin);
      if (co.checkout_url) window.location.href = co.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "plan_not_eligible") {
        setCreateErr(`This feature requires the Pro plan or higher (you're on ${detail.current_plan || "Starter"}).`);
      } else {
        setCreateErr("Couldn't create campaign. Please try again.");
      }
    } finally {
      setCreating(false);
    }
  };

  const checkoutExisting = async (campId) => {
    const co = await ncgeCheckout(campId, window.location.origin);
    if (co.checkout_url) window.location.href = co.checkout_url;
  };

  return (
    <div data-testid="contractor-grow-page" style={{ minHeight: "100vh", background: BG, padding: "32px 22px 80px" }}>
      <div style={{ maxWidth: 1160, margin: "0 auto" }}>
        {/* Header */}
        <p style={{ color: GOLD, letterSpacing: 4, fontSize: 13, fontFamily: "Bebas Neue, sans-serif", margin: 0 }}>
          NEARNOW · AUTONOMOUS CONTRACTOR GROWTH ENGINE
        </p>
        <h1 className="font-display" style={{ fontSize: 56, color: NAVY, letterSpacing: 1, margin: "6px 0 4px", lineHeight: 1.04 }}>
          GET MORE CUSTOMERS!
        </h1>
        <p style={{ color: NAVY, fontSize: 20, margin: 0 }}>
          Let <strong style={{ color: GOLD }}>NearNow.biz</strong> Advertise For You.
        </p>
        <p style={{ color: GREY, fontSize: 15, marginTop: 6 }}>
          Pro · Elite · Market Leader plans only. $150 minimum per campaign · $100 ad spend on Facebook + Instagram + $50 NearNow management fee.
        </p>

        {paidParam === "1" && (
          <div data-testid="grow-paid-banner" style={{ marginTop: 16, padding: 14, background: "#e6f5ec",
              color: GREEN, borderRadius: 10, fontSize: 15 }}>
            ✓ Payment received. Your campaign is in the review queue — NearNow will launch it within 24 hours.
          </div>
        )}
        {paidParam === "0" && (
          <div data-testid="grow-cancelled-banner" style={{ marginTop: 16, padding: 14, background: "#fde7e7",
              color: RED, borderRadius: 10, fontSize: 15 }}>
            Checkout cancelled. No charge made.
          </div>
        )}

        {/* LDS Section */}
        <Section title="STEP 1 · YOUR LOCAL DOMINANCE SCORE">
          {lds ? (
            <LdsCard lds={lds} onRecompute={() => setShowLdsForm(true)} />
          ) : (
            <div data-testid="lds-empty" style={{ textAlign: "center", padding: 26 }}>
              <p style={{ color: GREY, fontSize: 16, marginTop: 0 }}>
                Before we spend a dollar, we score where you stand across 7 pillars (Google Reviews, GBP, Website, Local SEO, Facebook, Instagram, Citation Consistency).
              </p>
              <button
                data-testid="compute-lds-btn"
                onClick={() => setShowLdsForm(true)}
                className="btn-gold tap"
                style={{ minHeight: 56, fontSize: 17, paddingLeft: 22, paddingRight: 22 }}
              >
                COMPUTE MY SCORE
              </button>
            </div>
          )}
          {showLdsForm && (
            <LdsForm
              overrides={overrides}
              setOverrides={setOverrides}
              onCompute={onCompute}
              loading={ldsLoading} err={ldsErr}
              onClose={() => setShowLdsForm(false)}
            />
          )}
        </Section>

        {/* Campaigns Section */}
        <Section title="STEP 2 · LAUNCH YOUR CAMPAIGN">
          {lds && lds.should_run_ads_yet === false && (
            <div style={{ marginBottom: 14, padding: 14, background: "#fef4e7", color: "#8a5a00",
                border: `1px solid ${GOLD}`, borderRadius: 10, fontSize: 14 }}>
              <strong>NearNow recommends fixing your fundamentals first.</strong><br/>
              {lds.should_run_ads_reason}
            </div>
          )}

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <button
              data-testid="create-campaign-btn"
              onClick={onCreate}
              disabled={creating || !lds}
              className="btn-gold tap"
              style={{ minHeight: 72, fontSize: 22, paddingLeft: 28, paddingRight: 28, letterSpacing: 1 }}
            >
              {creating ? "BUILDING YOUR CAMPAIGN…" : "🚀 LAUNCH AN AD — $150"}
            </button>
            {!lds && (
              <span style={{ color: GREY, fontSize: 14 }}>Compute your Local Dominance Score first ↑</span>
            )}
          </div>
          {createErr && (
            <div data-testid="create-error" style={{ marginTop: 12, padding: 12,
              background: "#fde7e7", color: RED, borderRadius: 8, fontSize: 14 }}>
              {createErr}
            </div>
          )}

          {/* Pricing breakdown */}
          <div style={{ marginTop: 18, padding: 16, background: BG, borderRadius: 10,
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, fontSize: 14 }}>
            <PriceTile label="AD SPEND" value="$100" color={NAVY} sub="On Facebook + Instagram" />
            <PriceTile label="NEARNOW FEE" value="$50" color={GOLD} sub="Strategy + creative + setup" />
            <PriceTile label="TOTAL ONE-TIME" value="$150" color={GREEN} sub="No subscription change" />
          </div>
        </Section>

        {/* Campaign list */}
        {campaigns.length > 0 && (
          <Section title="MY CAMPAIGNS">
            <table data-testid="my-campaigns-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, background: "#fff", borderRadius: 10, overflow: "hidden" }}>
              <thead>
                <tr style={{ background: BG }}>
                  <Th>NAME</Th><Th>STATUS</Th><Th align="right">PAID</Th><Th align="right">LEADS</Th><Th align="right">CREATED</Th><Th align="right"></Th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} data-testid={`campaign-row-${c.id}`} style={{ borderTop: `1px solid ${BG}` }}>
                    <td style={cell}>
                      <strong style={{ color: NAVY }}>{c.campaign_name || "Untitled"}</strong><br/>
                      <span style={{ fontSize: 12, color: GREY }}>{c.id.slice(0, 8)}…</span>
                    </td>
                    <td style={cell}><StatusBadge status={c.status} /></td>
                    <td style={{ ...cell, textAlign: "right" }}>{c.paid_at ? new Date(c.paid_at).toLocaleDateString() : "—"}</td>
                    <td style={{ ...cell, textAlign: "right" }}>{c.metrics?.leads ?? "—"}</td>
                    <td style={{ ...cell, textAlign: "right" }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                    <td style={{ ...cell, textAlign: "right" }}>
                      {c.status === "draft" || c.status === "pending_payment" ? (
                        <button onClick={() => checkoutExisting(c.id)} className="btn-gold tap"
                          style={{ minHeight: 36, fontSize: 12, padding: "0 14px" }}>
                          PAY $150
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}
      </div>
    </div>
  );
};

const LdsCard = ({ lds, onRecompute }) => {
  const overall = Number(lds.overall) || 0;
  return (
    <div data-testid="lds-card" style={{ background: "#fff", borderRadius: 12, padding: 22, boxShadow: "0 12px 30px rgba(10,22,40,0.06)" }}>
      <div style={{ display: "flex", gap: 28, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: "0 0 auto", textAlign: "center", minWidth: 180 }}>
          <div className="font-display" style={{ fontSize: 88, lineHeight: 1, color: NAVY }}>
            {overall.toFixed(1)}
          </div>
          <div style={{ color: GOLD, letterSpacing: 3, fontSize: 14, fontFamily: "Bebas Neue, sans-serif", marginTop: 4 }}>
            / 5.0
          </div>
          <div style={{ color: NAVY, fontSize: 16, fontWeight: 700, marginTop: 8, letterSpacing: 1 }}>
            {(lds.tier_label || "").toUpperCase()}
          </div>
        </div>
        <div style={{ flex: "1 1 360px" }}>
          <p style={{ margin: "0 0 10px", fontSize: 15, color: NAVY }}>
            <strong>RECOMMENDED AD ANGLE:</strong> {lds.recommended_ad_angle || "—"}
          </p>
          <p style={{ margin: "0 0 6px", color: GREY, fontSize: 13 }}>TOP 3 WINS THIS MONTH:</p>
          <ul style={{ margin: 0, paddingLeft: 20, color: NAVY, fontSize: 14, lineHeight: 1.6 }}>
            {(lds.top_3_wins_this_month || []).map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      </div>

      <div style={{ marginTop: 22, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        {Object.entries(lds.pillars || {}).map(([k, v]) => (
          <div key={k} data-testid={`pillar-${k}`} style={{ background: BG, padding: 12, borderRadius: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ color: GREY, fontSize: 11, letterSpacing: 1.5, fontFamily: "Bebas Neue, sans-serif" }}>
                {k.replace(/_/g, " ").toUpperCase()}
              </div>
              <div style={{ fontWeight: 700, color: v.score >= 4 ? GREEN : v.score <= 2 ? RED : NAVY }}>
                {v.score}/5
              </div>
            </div>
            <div style={{ fontSize: 13, color: NAVY, marginTop: 6 }}>{v.summary}</div>
            {v.fix && <div style={{ fontSize: 12, color: GOLD, marginTop: 4, fontStyle: "italic" }}>→ {v.fix}</div>}
          </div>
        ))}
      </div>

      <div style={{ textAlign: "right", marginTop: 14 }}>
        <button onClick={onRecompute} className="btn-outline-navy tap" style={{ minHeight: 36, fontSize: 12 }}>
          ↻ RECOMPUTE
        </button>
      </div>
    </div>
  );
};

const LdsForm = ({ overrides, setOverrides, onCompute, loading, err, onClose }) => (
  <div data-testid="lds-form" style={{ marginTop: 14, background: "#fff", padding: 22, borderRadius: 12, border: `2px solid ${GOLD}` }}>
    <h3 style={{ margin: 0, color: NAVY, fontSize: 18 }}>Provide what you can (optional but more accurate)</h3>
    <p style={{ color: GREY, fontSize: 13, marginTop: 4 }}>
      The AI will score with whatever it has. Empty fields are scored conservatively.
    </p>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
      {[
        ["website_url", "Your website (https://...)"],
        ["gbp_url", "Google Business Profile URL"],
        ["facebook_url", "Facebook page URL"],
        ["instagram_url", "Instagram handle URL"],
        ["google_reviews_count", "Google reviews count (number)"],
        ["google_reviews_avg", "Avg star rating (e.g. 4.7)"],
      ].map(([k, label]) => (
        <div key={k}>
          <label style={lbl}>{label}</label>
          <input
            data-testid={`lds-input-${k}`}
            style={inp} value={overrides[k] || ""}
            onChange={(e) => setOverrides({ ...overrides, [k]: e.target.value })}
          />
        </div>
      ))}
    </div>
    {err && (
      <div data-testid="lds-form-error" style={{ marginTop: 10, padding: 10, background: "#fde7e7", color: RED, borderRadius: 6, fontSize: 13 }}>
        {err === "no_emergent_llm_key" ? "AI scoring is offline — contact NearNow support." : err}
      </div>
    )}
    <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
      <button data-testid="lds-form-submit" onClick={onCompute} disabled={loading} className="btn-gold tap" style={{ minHeight: 48, flex: 1 }}>
        {loading ? "SCORING…" : "RUN MY SCORE"}
      </button>
      <button data-testid="lds-form-cancel" onClick={onClose} className="btn-outline-navy tap" style={{ minHeight: 48 }}>
        Cancel
      </button>
    </div>
  </div>
);

const PriceTile = ({ label, value, sub, color }) => (
  <div style={{ textAlign: "center" }}>
    <div style={{ color: GREY, letterSpacing: 2, fontSize: 11, fontFamily: "Bebas Neue, sans-serif" }}>{label}</div>
    <div className="font-display" style={{ color, fontSize: 38, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 12, color: GREY, marginTop: 2 }}>{sub}</div>
  </div>
);

const STATUS_STYLES = {
  draft: { bg: "#f0f0f0", color: "#5a5d66" },
  pending_payment: { bg: "#fef4e7", color: "#8a5a00" },
  pending_review: { bg: "#e7f0fe", color: "#1a4a8a" },
  approved: { bg: "#e6f5ec", color: GREEN },
  launched: { bg: "#0A1628", color: GOLD },
  completed: { bg: "#e6f5ec", color: GREEN },
  rejected: { bg: "#fde7e7", color: RED },
  archived: { bg: "#f0f0f0", color: GREY },
};
const StatusBadge = ({ status }) => {
  const sty = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span data-testid={`status-${status}`} style={{
      display: "inline-block", padding: "4px 10px", borderRadius: 999,
      background: sty.bg, color: sty.color, fontSize: 11, letterSpacing: 1.5,
      fontFamily: "Bebas Neue, sans-serif", textTransform: "uppercase",
    }}>{status.replace(/_/g, " ")}</span>
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginTop: 32 }}>
    <p style={{ color: GOLD, letterSpacing: 3, fontSize: 13, fontFamily: "Bebas Neue, sans-serif", margin: "0 0 10px" }}>
      {title}
    </p>
    {children}
  </div>
);

const Th = ({ children, align }) => (
  <th style={{ textAlign: align || "left", padding: "12px 14px", fontSize: 11,
    color: GREY, letterSpacing: 1.5, fontFamily: "Bebas Neue, sans-serif" }}>{children}</th>
);

const cell = { padding: "14px" };
const lbl = { fontSize: 11, color: GREY, letterSpacing: 1.5, fontFamily: "Bebas Neue, sans-serif", display: "block", marginBottom: 4 };
const inp = {
  width: "100%", minHeight: 44, fontSize: 14, padding: "0 12px",
  border: `1px solid #cbd0db`, borderRadius: 8, background: "#fff", outline: "none", boxSizing: "border-box",
};

export default ContractorGrow;
